// Security: consent → scopes → sealed token → one deploy job → your account → revoke.
// Scroll-scrubbed both ways; on small screens the camera pans so each stage stays readable.
import { clamp01, onFrame, smooth, state } from "./core.ts"

const L = (a: number, b: number, t: number) => a + (b - a) * t

export function initSecurity(): void {
  const sec = document.querySelector<HTMLElement>(".secflow")
  const svg = sec?.querySelector<SVGSVGElement>(".sec-svg")
  if (!sec || !svg) return
  const chips = [...svg.querySelectorAll<SVGElement>(".chip")]
  const key = svg.querySelector<SVGElement>(".key")
  const env = svg.querySelector<SVGElement>(".env")
  const flap = env?.querySelector<SVGElement>(".flap")
  const seal = env?.querySelector<SVGElement>(".seal")
  const job = svg.querySelector<SVGElement>(".job-g")
  const link = svg.querySelector<SVGElement>(".link")
  const sw = svg.querySelector<SVGElement>(".rvk")
  const knob = sw?.querySelector<SVGElement>(".swknob")
  const meter = sec.querySelector<HTMLElement>(".pin-meter")
  if (!key || !env || !flap || !seal || !job || !link || !sw || !knob) return
  let last = -1
  onFrame(() => {
    const r = sec.getBoundingClientRect()
    const p = state.reduced ? 1 : clamp01(-r.top / Math.max(1, r.height - innerHeight))
    if (Math.abs(p - last) < 0.0005) return
    last = p
    meter?.style.setProperty("--p", p.toFixed(4))
    chips.forEach((c, i) => {
      const v = smooth(0.04 + i * 0.06, 0.16 + i * 0.06, p)
      c.style.setProperty("--ci", v.toFixed(3))
      c.classList.toggle("on", v > 0.9 && !c.classList.contains("opt"))
    })
    // The token appears under the consent screen, then travels into the envelope.
    const kIn = smooth(0.28, 0.32, p)
    const kt = smooth(0.32, 0.42, p)
    const close = smooth(0.43, 0.49, p)
    const et = smooth(0.53, 0.63, p)
    const open = smooth(0.65, 0.69, p)
    const used = smooth(0.76, 0.81, p)
    const ex = L(560, 840, et)
    const ey = L(300, 290, et)
    const kx = kt < 1 ? L(240, ex, kt) : ex
    const ky = kt < 1 ? L(430, ey - 6, kt) : open > 0 ? ey - 6 - 78 * open : ey - 6
    key.setAttribute("transform", `translate(${kx.toFixed(1)} ${ky.toFixed(1)})`)
    key.style.opacity = (kIn * (kt >= 1 && open === 0 ? 0 : 1) * (1 - used)).toFixed(3)
    env.setAttribute(
      "transform",
      `translate(${ex.toFixed(1)} ${ey.toFixed(1)}) scale(${(1 - used * 0.4).toFixed(3)})`,
    )
    env.style.opacity = (smooth(0.38, 0.42, p) * (1 - used)).toFixed(3)
    const f = Math.max(0, close - open) // 0 = open flap (up), 1 = sealed (down)
    flap.setAttribute("transform", `translate(0 -37) scale(1 ${(-1 + 2 * f).toFixed(3)}) translate(0 37)`)
    seal.style.opacity = (smooth(0.48, 0.51, p) * (1 - open)).toFixed(3)
    job.classList.toggle("active", p > 0.6 && p < 0.82)
    job.style.setProperty("--jp", smooth(0.69, 0.77, p).toFixed(3))
    const ld = smooth(0.8, 0.86, p)
    const al = smooth(0.84, 0.9, p)
    const rv = smooth(0.92, 0.97, p)
    link.style.setProperty("--ld", ld.toFixed(3))
    link.style.setProperty("--rv", rv.toFixed(3))
    link.classList.toggle("cut", rv > 0.5)
    svg.style.setProperty("--al", al.toFixed(3))
    svg.style.setProperty("--rv", rv.toFixed(3))
    sw.classList.toggle("off", rv > 0.5)
    knob.setAttribute("cx", L(1158, 1126, rv).toFixed(1))
    // Small screens: the camera pans across the diagram so every stage stays readable.
    if (svg.getBoundingClientRect().width < 760 && !state.reduced) {
      const cx =
        p < 0.3
          ? 240
          : p < 0.5
            ? L(240, 560, (p - 0.3) / 0.2)
            : p < 0.7
              ? L(560, 840, (p - 0.5) / 0.2)
              : L(840, 1225, smooth(0.7, 0.86, p))
      svg.setAttribute("viewBox", `${Math.max(0, Math.min(1400 - 560, cx - 280)).toFixed(1)} 60 560 470`)
    } else svg.setAttribute("viewBox", "0 0 1400 520")
  })
}

/** Pricing: the rent-versus-own bars fill as they scroll in. */
export function initBars(): void {
  const rows = document.querySelectorAll(".brow")
  if (!rows.length) return
  if (state.reduced) {
    for (const r of rows) r.classList.add("in")
    return
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add("in")
          io.unobserve(e.target)
        }
      }
    },
    { rootMargin: "0px 0px -8% 0px" },
  )
  for (const r of rows) io.observe(r)
}
